import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, AlertTriangle, CheckCircle2, UserPlus, FileText,
  Phone, Calendar, MessageCircle, Sparkles, Bot,
} from "lucide-react";
import { useAuth } from "../lib/authContext";
import { inboxApi, inboxLabels, formatRelative } from "../lib/inboxApi";
import { Textarea } from "../components/ui/textarea";

export default function AdminInboxDetail() {
  const { id } = useParams();
  const { isAuthenticated, user, isChecking } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'assume'|'resolve'|'escalate'|'note'
  const [noteDraft, setNoteDraft] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await inboxApi.detail(id);
      setData(d);
      setNoteDraft(d.interaction?.nota_interna || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar registro.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (isAuthenticated && user?.role === "admin") load(); }, [load, isAuthenticated, user]);

  if (isChecking) return <div className="max-w-3xl mx-auto p-8 text-navy/55">Verificando…</div>;
  if (!isAuthenticated) return <Navigate to={`/entrar?next=/admin/inbox/${id}`} replace />;
  if (user?.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="inbox-detail-no-access">
        <p className="font-display text-2xl text-navy">Acesso restrito</p>
        <p className="text-sm text-navy/55 mt-2">Apenas administradores podem ver o inbox.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-8 text-navy/55" data-testid="inbox-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Carregando…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="inbox-detail-missing">
        <p className="text-navy/65">Registro não encontrado.</p>
        <Link to="/admin" className="text-gold underline text-sm mt-3 inline-block">Voltar ao painel</Link>
      </div>
    );
  }

  const { interaction: it, user: contact, history } = data;
  const isCrisis = it.escalate || it.type === "CRISE";

  const runAction = async (key, fn, successMsg) => {
    if (busy) return;
    setBusy(key);
    try {
      const updated = await fn();
      setData((d) => ({ ...d, interaction: updated }));
      toast.success(successMsg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na operação.");
    } finally {
      setBusy(null);
    }
  };

  const handleAssume = () => runAction("assume", () => inboxApi.assume(id), "Atendimento assumido.");
  const handleResolve = () => runAction("resolve", () => inboxApi.resolve(id), "Marcado como respondido.");
  const handleEscalate = () => runAction("escalate", () => inboxApi.escalate(id), "Escalado para o pastor.");
  const handleNote = () => runAction("note", () => inboxApi.note(id, noteDraft), "Nota salva.");

  return (
    <div className="max-w-6xl mx-auto section-padding py-8" data-testid="inbox-detail-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/65 hover:text-gold transition-colors mb-6"
        data-testid="inbox-detail-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar ao painel
      </Link>

      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Inbox · Detalhe</p>
          <h1 className="font-display text-3xl text-navy mt-1">{contact.name}</h1>
          <p className="text-sm text-navy/65 mt-1 flex items-center gap-2">
            <Phone className="w-3.5 h-3.5" /> {contact.phone}
            <span className="mx-2 text-navy/25">·</span>
            <Calendar className="w-3.5 h-3.5" /> {formatRelative(contact.first_seen)}
            <span className="mx-2 text-navy/25">·</span>
            <span data-testid="inbox-detail-history-count">{contact.total_interactions} interaç{contact.total_interactions === 1 ? "ão" : "ões"}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-block px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-widest ${inboxLabels.TYPE_TONE[it.type]}`}>
            {inboxLabels.TYPE_LABEL[it.type]}
          </span>
          <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest ${inboxLabels.STATUS_TONE[it.status]}`}>
            {inboxLabels.STATUS_LABEL[it.status]}
          </span>
          {it.priority === "urgente" && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[10px] uppercase tracking-widest">
              urgente
            </span>
          )}
          {isCrisis && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-700 border border-rose-500/40 text-[10px] uppercase tracking-widest">
              <AlertTriangle className="w-3 h-3" /> alerta criado
            </span>
          )}
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Conversa */}
        <section className="bg-white border border-border rounded-2xl p-5 sm:p-6" data-testid="inbox-detail-thread">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4">Conversa completa</p>
          {history.length === 0 ? (
            <p className="text-sm text-navy/55">Sem mensagens.</p>
          ) : (
            <div className="space-y-3">
              {history.map((m, idx) => (
                <ChatMessages key={m.id} item={m} highlight={m.id === it.id} idx={idx} />
              ))}
            </div>
          )}

          {it.escalate_reason && (
            <div className="mt-6 bg-rose-50 border border-rose-200 rounded-xl p-4" data-testid="inbox-detail-escalate-reason">
              <p className="text-[10px] uppercase tracking-widest text-rose-700 mb-1.5 font-medium">Motivo da escalada</p>
              <p className="text-sm text-rose-900">{it.escalate_reason}</p>
            </div>
          )}
        </section>

        {/* Painel lateral de ações */}
        <aside className="space-y-4">
          {it.prayer_registered && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="inbox-detail-prayer">
              <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-medium">Pedido de oração registrado</p>
              <p className="text-[12px] text-emerald-900 mt-1.5">Esta interação foi marcada como pedido de oração.</p>
            </div>
          )}

          <div className="bg-white border border-border rounded-2xl p-5 space-y-2.5" data-testid="inbox-detail-actions">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Ações</p>

            <ActionButton
              testid="inbox-action-assume"
              icon={<UserPlus className="w-3.5 h-3.5" />}
              label="Assumir atendimento"
              loading={busy === "assume"}
              disabled={it.status === "andamento" && it.assigned_to === user.id}
              onClick={handleAssume}
              tone="navy"
            />
            <ActionButton
              testid="inbox-action-resolve"
              icon={<CheckCircle2 className="w-3.5 h-3.5" />}
              label="Marcar como respondido"
              loading={busy === "resolve"}
              disabled={it.status === "respondido"}
              onClick={handleResolve}
              tone="emerald"
            />
            <ActionButton
              testid="inbox-action-escalate"
              icon={<AlertTriangle className="w-3.5 h-3.5" />}
              label="Escalar para pastor"
              loading={busy === "escalate"}
              disabled={it.status === "escalado"}
              onClick={handleEscalate}
              tone="rose"
            />
          </div>

          <div className="bg-white border border-border rounded-2xl p-5" data-testid="inbox-detail-note">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> Nota interna
            </p>
            <p className="text-[11px] text-navy/55 mb-2.5">Visível apenas para a equipe pastoral.</p>
            <Textarea
              rows={4}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              maxLength={2000}
              data-testid="inbox-note-input"
              placeholder="Ex.: Já entrei em contato pelo telefone, retornará amanhã…"
              className="bg-[hsl(var(--parchment))] border-transparent focus:bg-white text-sm"
            />
            <button
              type="button"
              onClick={handleNote}
              disabled={busy === "note" || (noteDraft || "") === (it.nota_interna || "")}
              data-testid="inbox-note-save"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              {busy === "note" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Salvar nota
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ChatMessages({ item, highlight, idx }) {
  return (
    <div data-testid={`inbox-detail-msg-${idx}`} className={highlight ? "ring-2 ring-gold/40 rounded-xl p-1" : ""}>
      {/* User message, right */}
      <div className="flex items-end justify-end gap-2 mb-2">
        <div className="bg-navy text-white rounded-2xl rounded-br-md px-4 py-2.5 max-w-[75%]" data-testid={`inbox-msg-user-${idx}`}>
          <p className="text-[11px] opacity-70 mb-1 flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> Usuário · {formatRelative(item.created_at)}
          </p>
          <p className="text-sm whitespace-pre-line">{item.message}</p>
        </div>
      </div>
      {/* AI response, left */}
      {item.ai_response && (
        <div className="flex items-end gap-2">
          <div className="bg-[hsl(var(--parchment))] text-navy rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[75%] border border-gold/20" data-testid={`inbox-msg-ai-${idx}`}>
            <p className="text-[11px] text-navy/55 mb-1 flex items-center gap-1">
              <Bot className="w-3 h-3" /> Assistente CM · {inboxLabels.TYPE_LABEL[item.type]}
            </p>
            <p className="text-sm whitespace-pre-line">{item.ai_response}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionButton({ testid, icon, label, onClick, loading, disabled, tone }) {
  const tones = {
    navy: "bg-navy text-white hover:bg-gold hover:text-navy",
    emerald: "bg-emerald-600 text-white hover:bg-emerald-700",
    rose: "bg-rose-600 text-white hover:bg-rose-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={loading || disabled}
      className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${tones[tone] || tones.navy}`}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
      {label}
    </button>
  );
}
